import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/AppShell";
import { fmtEur } from "@/lib/format";
import { FileText, ExternalLink, Trash2, Download, Copy, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: { label: "Bozza", color: "#94A3B8" },
  sent: { label: "Inviato", color: "#0EA5C4" },
  viewed: { label: "Visualizzato", color: "#E0A82E" },
  accepted: { label: "Accettato", color: "#22A85A" },
  rejected: { label: "Rifiutato", color: "#E11D48" },
};

export default function Quotes() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState({});

  const reload = async () => {
    const [q, c] = await Promise.all([api.get("/quotes"), api.get("/customers")]);
    setList(q.data);
    setCustomers(Object.fromEntries(c.data.map((x) => [x.id, x])));
  };
  useEffect(() => { reload(); }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminare il preventivo?")) return;
    await api.delete(`/quotes/${id}`); reload();
  };

  const sendEmail = async (q) => {
    const c = customers[q.customer_id];
    if (!c?.email) { toast.error("Il cliente non ha un'email"); return; }
    try {
      await api.post(`/quotes/${q.id}/send`);
      toast.success(`Preventivo inviato a ${c.email}`);
      reload();
    } catch (e) { toast.error("Invio non riuscito"); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/p/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiato");
  };

  const downloadPdf = async (q) => {
    const r = await fetch(`${API_BASE}/quotes/${q.id}/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem("sf_token")}` }});
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${q.quote_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="page-quotes">
      <PageHeader eyebrow="Vendita" title="Preventivi" subtitle={`${list.length} preventivi totali · genera nuovi dal Simulatore`} />
      <div className="px-8 py-6">
        {list.length === 0 && (
          <div className="bg-white border border-dashed border-zinc-300 rounded-3xl p-12 text-center">
            <div className="w-12 h-12 rounded-2xl bg-zinc-100 text-zinc-400 flex items-center justify-center mx-auto mb-4"><FileText size={22}/></div>
            <div className="font-semibold text-zinc-900">Nessun preventivo ancora.</div>
            <div className="text-sm text-zinc-500 mt-2">Apri il <Link to="/app/simulator" className="text-zinc-900 font-medium underline">Simulatore</Link> da un cliente, calcola il layout e genera il preventivo.</div>
          </div>
        )}
        <div className="grid gap-3">
          {list.map((q) => {
            const c = customers[q.customer_id];
            const cname = c ? (c.business_name || `${c.first_name} ${c.last_name}`.trim()) : "—";
            const st = STATUS[q.status] || STATUS.draft;
            return (
              <div key={q.id} className="bg-white border border-zinc-200/70 rounded-2xl p-5 flex items-center gap-5 hover:border-zinc-300 transition-colors" data-testid="quote-row">
                <Link to={`/app/quotes/${q.id}`} className="flex-1 min-w-0" data-testid={`open-quote-${q.id}`}>
                  <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                    <span className="font-mono text-xs text-zinc-400">{q.quote_number}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full" style={{ color: st.color, background: `${st.color}1a` }}>{st.label}</span>
                    {q.viewed_at && <span className="text-[10px] text-sky-600 font-mono">visto · {q.viewed_at?.slice(5,16).replace("T"," ")}</span>}
                  </div>
                  <div className="font-semibold text-zinc-900 truncate">{cname}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{q.created_at?.slice(0,10)} · {q.items.length} voci</div>
                </Link>
                <div className="text-right shrink-0">
                  <div className="font-mono text-[10px] text-zinc-400 uppercase tracking-wider">Totale</div>
                  <div className="font-mono text-2xl font-semibold text-zinc-900">{fmtEur(q.total)}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => sendEmail(q)} title="Invia email" className="p-2 rounded-lg text-zinc-400 hover:bg-emerald-50 hover:text-emerald-600 transition-colors" data-testid={`send-${q.id}`}><Send size={15}/></button>
                  <button onClick={() => copyLink(q.public_token)} title="Copia link" className="p-2 rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors" data-testid={`copy-${q.id}`}><Copy size={15}/></button>
                  <a href={`/p/${q.public_token}`} target="_blank" rel="noreferrer" title="Apri online" className="p-2 rounded-lg text-zinc-400 hover:bg-sky-50 hover:text-sky-600 transition-colors" data-testid={`view-${q.id}`}><ExternalLink size={15}/></a>
                  <button onClick={() => downloadPdf(q)} title="PDF" className="p-2 rounded-lg text-zinc-400 hover:bg-amber-50 hover:text-amber-600 transition-colors" data-testid={`pdf-${q.id}`}><Download size={15}/></button>
                  <button onClick={() => del(q.id)} className="p-2 rounded-lg text-zinc-400 hover:bg-rose-50 hover:text-rose-500 transition-colors" data-testid={`delete-${q.id}`}><Trash2 size={15}/></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
